/**
 * Os campos das duas abas da Calculadora Petrella e o que muda neles.
 *
 * Papel: aqui ficam os campos de "Ajuda de Custo" como estão na tela
 * (`CamposDaAjudaDeCusto`), os de quando a caixa abre, o que digitar o
 * funcionário preenche (`aoEscolherFuncionario`), o que "Repetir último
 * lançamento" copia do grupo (`repetirGrupo`), a troca de mês ou de ano que
 * refaz a data, e a leitura dos cartões para as fórmulas do pacote
 * compartilhado (`entradaDaCalculadora`); e os campos de "Diaristas"
 * (`CamposDaDiarista`), com a diária que o perfil preenche
 * (`aoEscolherDiarista`).
 *
 * Como usar: a caixa guarda `CamposDaAjudaDeCusto` no estado e passa por estas
 * funções a cada mudança; os subtotais e os itens saem de
 * `subtotaisDaCalculadora` e `itensDaCalculadora` sobre `entradaDaCalculadora`,
 * nunca dos textos direto. Um cartão escondido continua contando se estiver
 * marcado, como na origem: esconder só some com ele da tela.
 *
 * Um favorecido sem nenhum tipo de ajuda habilitado não esconde cartão nenhum:
 * os quatro continuam à vista, como para o nome que não está no cadastro.
 */
import {
  type AjudaCustoDoFavorecido,
  type EntradaDaCalculadora,
  type Favorecido,
  type GrupoAjudaCusto,
  type NomeDaFolha,
  type TipoItemAjudaCusto,
  dataDePagamentoDaFolha,
  valorDaDiarista,
} from '@concierge/shared';

import { formatarCpf } from '@/shared/lib/formato';
import { lerValorDigitado } from '@/shared/lib/mascara';

/** Os quatro tipos, na ordem dos cartões. */
export const TIPOS_DA_CALCULADORA: readonly TipoItemAjudaCusto[] = [
  'custo',
  'diarias',
  'medica',
  'inss',
];

/** Um cartão com diária como está na tela. */
export interface CartaoComDiaria {
  incluido: boolean;
  /** A diária escrita, com até quatro casas. */
  diaria: string;
  /** Os dias escritos. */
  dias: string;
}

/** Um cartão de valor fixo como está na tela. */
export interface CartaoFixo {
  incluido: boolean;
  /** O valor escrito, com a máscara de centavos. */
  valor: string;
}

/** A aba "Ajuda de Custo" como está na tela. */
export interface CamposDaAjudaDeCusto {
  funcionario: string;
  cpf: string;
  pagador: string;
  /** O mês de 1 a 12, como o valor da lista. */
  mes: string;
  ano: string;
  /** A data do lançamento, em `AAAA-MM-DD`, ou vazia. */
  data: string;
  ferias: boolean;
  custo: CartaoComDiaria;
  diarias: CartaoComDiaria;
  medica: CartaoFixo;
  inss: CartaoFixo;
  /** Os tipos com cartão à vista; `null` mostra os quatro. */
  visiveis: readonly TipoItemAjudaCusto[] | null;
}

/** O cartão com diária desmarcado e vazio, com os dias em 30. */
const CARTAO_COM_DIARIA_VAZIO: CartaoComDiaria = { incluido: false, diaria: '', dias: '30' };

/** O cartão de valor fixo desmarcado e vazio. */
const CARTAO_FIXO_VAZIO: CartaoFixo = { incluido: false, valor: '' };

/**
 * Escreve uma diária ou um valor como a origem preenche os campos
 * (`_acFmtDiaria`, l. 5015).
 *
 * @param valor - O número.
 * @returns Em `pt-BR`, com duas a quatro casas: `83,3333`, `1.000,00`.
 */
export function formatarDiaria(valor: number): string {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

/**
 * A data do lançamento de um mês e um ano escritos.
 *
 * @param mes - O mês, de 1 a 12.
 * @param ano - O ano escrito.
 * @returns O dia de pagamento da folha; `null` quando falta mês ou ano, e a
 *   data à vista não muda (l. 4768–4773).
 */
export function dataDoMesEAno(mes: string, ano: string): string | null {
  const numeroDoMes = Number.parseInt(mes, 10);
  const numeroDoAno = Number.parseInt(ano, 10);
  if (!numeroDoMes || !numeroDoAno) return null;
  return dataDePagamentoDaFolha(numeroDoAno, numeroDoMes);
}

/**
 * Os campos de quando a caixa abre (l. 4775–4793).
 *
 * @param hoje - O dia de hoje, em `AAAA-MM-DD`.
 * @param pagadorPadrao - O pagador da configuração da casa.
 * @returns Tudo vazio e desmarcado, os dias em 30, o mês e o ano de hoje e a
 *   data no dia de pagamento dele.
 */
export function camposIniciaisDaAjudaDeCusto(
  hoje: string,
  pagadorPadrao: string,
): CamposDaAjudaDeCusto {
  const mes = String(Number(hoje.slice(5, 7)));
  const ano = hoje.slice(0, 4);
  return {
    funcionario: '',
    cpf: '',
    pagador: pagadorPadrao,
    mes,
    ano,
    data: dataDoMesEAno(mes, ano) ?? '',
    ferias: false,
    custo: CARTAO_COM_DIARIA_VAZIO,
    diarias: CARTAO_COM_DIARIA_VAZIO,
    medica: CARTAO_FIXO_VAZIO,
    inss: CARTAO_FIXO_VAZIO,
    visiveis: null,
  };
}

/**
 * Troca o mês ou o ano e refaz a data.
 *
 * @param campos - Os campos.
 * @param mudanca - O mês ou o ano novo.
 * @returns Os campos com a data no dia de pagamento do mês novo; a data fica
 *   como está quando falta mês ou ano.
 */
export function comMesEAno<Campos extends { mes: string; ano: string; data: string }>(
  campos: Campos,
  mudanca: { mes?: string; ano?: string },
): Campos {
  const proximos = { ...campos, ...mudanca };
  return { ...proximos, data: dataDoMesEAno(proximos.mes, proximos.ano) ?? proximos.data };
}

/**
 * Os tipos habilitados no perfil de um favorecido.
 *
 * @param ajudaCusto - A ajuda de custo do perfil.
 * @returns Os tipos com valor no perfil, na ordem dos cartões.
 */
export function tiposHabilitados(ajudaCusto: AjudaCustoDoFavorecido): TipoItemAjudaCusto[] {
  const valores: Readonly<Record<TipoItemAjudaCusto, number | null>> = {
    custo: ajudaCusto.custoDiaria,
    diarias: ajudaCusto.diariasDiaria,
    medica: ajudaCusto.medicaValor,
    inss: ajudaCusto.inssValor,
  };
  return TIPOS_DA_CALCULADORA.filter((tipo) => valores[tipo] !== null);
}

/**
 * Os favorecidos que a lista do Funcionário sugere (l. 4979–4984).
 *
 * @param favorecidos - Os favorecidos da casa.
 * @returns Os que têm algum tipo de ajuda habilitado.
 */
export function funcionariosDaCalculadora(favorecidos: readonly Favorecido[]): Favorecido[] {
  return favorecidos.filter(
    (favorecido) => tiposHabilitados(favorecido.ajudaCusto).length > 0,
  );
}

/**
 * O favorecido cujo nome casa com o digitado.
 *
 * @param favorecidos - Os favorecidos da casa.
 * @param nome - O nome digitado.
 * @returns O favorecido com o mesmo nome, sem diferenciar maiúsculas e sem os
 *   espaços das pontas; `undefined` com o campo vazio ou sem nenhum que case.
 */
export function favorecidoPeloNome(
  favorecidos: readonly Favorecido[],
  nome: string,
): Favorecido | undefined {
  const alvo = nome.trim().toLowerCase();
  if (alvo === '') return undefined;
  return favorecidos.find((favorecido) => favorecido.nome.trim().toLowerCase() === alvo);
}

/**
 * Mostra só os cartões do perfil, marcando os visíveis e desmarcando os outros
 * (`filtrarCardsAjudaCustoPorFavorecido`, l. 4990–5013).
 *
 * @param campos - Os campos.
 * @param favorecido - O favorecido do nome digitado, quando há.
 * @returns Os campos com os cartões filtrados; sem favorecido, ou sem tipo
 *   habilitado, os quatro à vista e as marcas como estavam.
 */
function filtrarCartoes(
  campos: CamposDaAjudaDeCusto,
  favorecido: Favorecido | undefined,
): CamposDaAjudaDeCusto {
  const tipos = favorecido === undefined ? [] : tiposHabilitados(favorecido.ajudaCusto);
  if (tipos.length === 0) return { ...campos, visiveis: null };
  return {
    ...campos,
    visiveis: tipos,
    custo: { ...campos.custo, incluido: tipos.includes('custo') },
    diarias: { ...campos.diarias, incluido: tipos.includes('diarias') },
    medica: { ...campos.medica, incluido: tipos.includes('medica') },
    inss: { ...campos.inss, incluido: tipos.includes('inss') },
  };
}

/**
 * O que digitar o funcionário faz (`autoPreencherCpfAjudaCusto`, l. 5017–5051).
 *
 * @param campos - Os campos, com o nome já trocado.
 * @param favorecidos - Os favorecidos da casa.
 * @param nomes - O mapa CPF→nome da casa, para o CPF que o perfil não tem.
 * @returns Os cartões filtrados; o CPF vazio preenchido pelo do perfil ou pelo
 *   do mapa; e cada valor vazio preenchido pelo do perfil, com o cartão
 *   marcado.
 */
export function aoEscolherFuncionario(
  campos: CamposDaAjudaDeCusto,
  favorecidos: readonly Favorecido[],
  nomes: readonly NomeDaFolha[],
): CamposDaAjudaDeCusto {
  const favorecido = favorecidoPeloNome(favorecidos, campos.funcionario);
  const proximos = filtrarCartoes(campos, favorecido);
  const alvo = campos.funcionario.trim().toLowerCase();
  if (alvo === '') return proximos;

  if (proximos.cpf.trim() === '') {
    const cpf =
      favorecido?.cpf ?? nomes.find((par) => par.nome.toLowerCase() === alvo)?.cpf ?? null;
    if (cpf !== null) proximos.cpf = formatarCpf(cpf);
  }
  if (favorecido === undefined) return proximos;

  const { custoDiaria, diariasDiaria, medicaValor, inssValor } = favorecido.ajudaCusto;
  for (const [tipo, valor] of [
    ['custo', custoDiaria],
    ['diarias', diariasDiaria],
  ] as const) {
    if (valor !== null && proximos[tipo].diaria.trim() === '') {
      proximos[tipo] = { ...proximos[tipo], diaria: formatarDiaria(valor), incluido: true };
    }
  }
  for (const [tipo, valor] of [
    ['medica', medicaValor],
    ['inss', inssValor],
  ] as const) {
    if (valor !== null && proximos[tipo].valor.trim() === '') {
      proximos[tipo] = { ...proximos[tipo], valor: formatarDiaria(valor), incluido: true };
    }
  }
  return proximos;
}

/**
 * Copia o último grupo do funcionário para a calculadora
 * (`repetirUltimoLancamentoAjudaCusto`, l. 4922–4974).
 *
 * @param campos - Os campos.
 * @param grupo - O grupo mais recente do favorecido.
 * @param favorecido - O favorecido do nome digitado, para filtrar os cartões.
 * @param hoje - O dia de hoje, em `AAAA-MM-DD`.
 * @returns O CPF e o pagador do grupo (quando ele os tem), as férias, os quatro
 *   cartões zerados e depois remarcados com a diária e os dias de cada item, e
 *   o mês e o ano de hoje com a data refeita. O item sem diária tem a diária
 *   tirada do valor, desfazendo o 1/3 de férias quando o grupo o tinha.
 */
export function repetirGrupo(
  campos: CamposDaAjudaDeCusto,
  grupo: GrupoAjudaCusto,
  favorecido: Favorecido | undefined,
  hoje: string,
): CamposDaAjudaDeCusto {
  const filtrados = filtrarCartoes(
    {
      ...campos,
      cpf: grupo.cpf === null || grupo.cpf === '' ? campos.cpf : formatarCpf(grupo.cpf),
      pagador: grupo.pagador === '' ? campos.pagador : grupo.pagador,
      ferias: grupo.ferias,
    },
    favorecido,
  );
  const proximos: CamposDaAjudaDeCusto = {
    ...filtrados,
    custo: CARTAO_COM_DIARIA_VAZIO,
    diarias: CARTAO_COM_DIARIA_VAZIO,
    medica: CARTAO_FIXO_VAZIO,
    inss: CARTAO_FIXO_VAZIO,
  };
  const multiplicador = grupo.ferias ? 4 / 3 : 1;
  for (const item of grupo.itens) {
    if (item.tipo === 'custo' || item.tipo === 'diarias') {
      const diaria =
        item.valorDiaria ?? (item.dias ? item.valor / (item.dias * multiplicador) : 0);
      proximos[item.tipo] = {
        incluido: true,
        diaria: formatarDiaria(diaria),
        dias: String(item.dias || 30),
      };
    } else {
      proximos[item.tipo] = { incluido: true, valor: formatarDiaria(item.valor) };
    }
  }
  return comMesEAno(proximos, {
    mes: String(Number(hoje.slice(5, 7))),
    ano: hoje.slice(0, 4),
  });
}

/**
 * Lê os cartões para as fórmulas da calculadora.
 *
 * @param campos - Os campos como estão na tela.
 * @returns As férias e cada cartão marcado com os números lidos; o desmarcado
 *   é `null`. Os dias vazios valem zero.
 */
export function entradaDaCalculadora(campos: CamposDaAjudaDeCusto): EntradaDaCalculadora {
  const comDiaria = (cartao: CartaoComDiaria) =>
    cartao.incluido
      ? {
          valorDiaria: lerValorDigitado(cartao.diaria),
          dias: Number.parseFloat(cartao.dias) || 0,
        }
      : null;
  const fixo = (cartao: CartaoFixo) =>
    cartao.incluido ? { valor: lerValorDigitado(cartao.valor) } : null;
  return {
    ferias: campos.ferias,
    custo: comDiaria(campos.custo),
    diarias: comDiaria(campos.diarias),
    medica: fixo(campos.medica),
    inss: fixo(campos.inss),
  };
}

/** A aba "Diaristas" como está na tela. */
export interface CamposDaDiarista {
  diarista: string;
  pagador: string;
  /** O mês de 1 a 12, como o valor da lista. */
  mes: string;
  ano: string;
  /** A data do lançamento, em `AAAA-MM-DD`, ou vazia. */
  data: string;
  /** A diária escrita, com até quatro casas. */
  diaria: string;
  /** Os dias escritos, que aceitam fração. */
  dias: string;
}

/**
 * Os campos da aba "Diaristas" de quando a caixa abre (l. 4795–4805).
 *
 * @param hoje - O dia de hoje, em `AAAA-MM-DD`.
 * @param pagadorPadrao - O pagador da configuração da casa.
 * @returns Diarista, diária e dias vazios — os dias não têm valor inicial —, o
 *   mês e o ano de hoje e a data no dia de pagamento dele.
 */
export function camposIniciaisDaDiarista(
  hoje: string,
  pagadorPadrao: string,
): CamposDaDiarista {
  const mes = String(Number(hoje.slice(5, 7)));
  const ano = hoje.slice(0, 4);
  return {
    diarista: '',
    pagador: pagadorPadrao,
    mes,
    ano,
    data: dataDoMesEAno(mes, ano) ?? '',
    diaria: '',
    dias: '',
  };
}

/**
 * As diaristas que a lista sugere (l. 4851–4856).
 *
 * @param favorecidos - Os favorecidos da casa.
 * @returns Os que têm diarista habilitada no perfil.
 */
export function diaristasDaCalculadora(favorecidos: readonly Favorecido[]): Favorecido[] {
  return favorecidos.filter((favorecido) => favorecido.diaristaHabilitada);
}

/**
 * O que digitar a diarista faz (`autoPreencherDiarista`, l. 4860–4870).
 *
 * @param campos - Os campos, com o nome já trocado.
 * @param favorecidos - Os favorecidos da casa.
 * @returns A diária vazia preenchida pela do perfil, quando o nome casa com uma
 *   diarista habilitada que tem diária; os dias nunca mudam.
 */
export function aoEscolherDiarista(
  campos: CamposDaDiarista,
  favorecidos: readonly Favorecido[],
): CamposDaDiarista {
  const favorecido = favorecidoPeloNome(favorecidos, campos.diarista);
  if (
    favorecido === undefined ||
    !favorecido.diaristaHabilitada ||
    favorecido.diaristaValorDiaria === null ||
    campos.diaria.trim() !== ''
  ) {
    return campos;
  }
  return { ...campos, diaria: formatarDiaria(favorecido.diaristaValorDiaria) };
}

/**
 * O subtotal e o total da aba "Diaristas".
 *
 * @param campos - Os campos como estão na tela.
 * @returns `diária × dias` arredondado; o vazio vale zero.
 */
export function valorDosCamposDaDiarista(campos: CamposDaDiarista): number {
  return valorDaDiarista(lerValorDigitado(campos.diaria), Number.parseFloat(campos.dias) || 0);
}
